import type { ResolvedAgentUiAction } from '@hector/shared-types';
import type { DocumentViewerOptions } from '@/services/documentViewerService';

type PreviewAction = Extract<ResolvedAgentUiAction, { id: 'documents.preview' }>;

interface AuthoritativeDocumentRow {
  readonly id?: string;
  readonly name?: string;
  readonly file_path?: string | null;
  readonly document_type?: string | null;
  readonly is_folder?: boolean;
  readonly original_document_id?: string | null;
  [key: string]: unknown;
}

export interface DocumentPreviewDependencies {
  readonly cabinetId: string | null;
  readonly get: (path: string) => Promise<AuthoritativeDocumentRow[]>;
  readonly canPreviewDocument: (name: string, type?: string, filePath?: string) => boolean;
  readonly viewDocuments: (documents: DocumentViewerOptions[]) => Promise<void>;
}

export async function previewDocuments(
  action: PreviewAction,
  dependencies: DocumentPreviewDependencies,
): Promise<void> {
  if (!dependencies.cabinetId) {
    throw new Error(`Cannot preview dossier documents: actionId=${action.id}; caseId=${action.caseId}; current cabinet is unavailable. Select a cabinet and retry.`);
  }

  // The action carries identifiers only. Always resolve their current rows at click time.
  const rows = await dependencies.get(`/cases/${action.caseId}/documents`);
  const byId = new Map(rows.flatMap((row) => row.id ? [[row.id, row] as const] : []));
  const missingIds = action.documentIds.filter((id) => !byId.has(id));
  if (missingIds.length > 0) {
    throw new Error(`Cannot preview dossier documents: caseId=${action.caseId}; missing documentIds=${missingIds.join(',')}. Refresh the conversation and retry.`);
  }

  const options = action.documentIds.map((documentId): DocumentViewerOptions => {
    const row = byId.get(documentId);
    if (!row) throw new Error(`Cannot preview dossier document after exact match: documentId=${documentId}.`);
    const filePath = row.file_path?.trim();
    const fileName = row.name?.trim();
    const fileType = row.document_type ?? undefined;
    const structurallyPreviewable = row.is_folder !== true
      && row.document_type !== 'synthesis'
      && row.original_document_id == null
      && Boolean(filePath)
      && Boolean(fileName);
    if (!structurallyPreviewable || !dependencies.canPreviewDocument(fileName ?? '', fileType, filePath)) {
      throw new Error(`Cannot preview dossier document: caseId=${action.caseId}; documentId=${documentId} is not previewable in its current authoritative state. Choose a supported file.`);
    }
    return { filePath: filePath!, fileName: fileName!, fileType, caseId: action.caseId, documentId };
  });

  await dependencies.viewDocuments(options);
}
